import { DownOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import {
  useApproveExpense,
  useRejectExpense,
} from '@nxsys/hooks/api/dashboard';
import { AppState } from '@nxsys/store';
import { dashboardActions } from '@nxsys/store/slices/dashboard/reducer/dashboardSlice';
import { GetExpenseListParams } from '@nxsys/store/slices/dashboard/types';
import { IExpense } from '@nxsys/types/models/expense';
import { showCurrencyByCountry } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { formatMoney } from '@nxsys/utils/number-helper';
import { Dropdown } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { debounce } from 'lodash';
import RejectExpenseModal, {
  RejectExpenseValues,
} from '../../employees/detail/components/RejectExpenseModal';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type Props = {
  isOpen: boolean;
  toggle: () => void;
  cbAfterAction: () => void;
};

const ExpenseModal: React.FC<Props> = ({ isOpen, toggle, cbAfterAction }) => {
  const dispatch = useDispatch();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const { expenseList } = useSelector(
    (state: AppState) => state.dashboardStore,
  );
  const { list, pagination, loading, queryParams } = expenseList;

  const [selectedRows, setSelectedRows] = useState<IExpense[]>([]);

  useEffect(() => {
    if (isOpen)
      handleFetchData({
        page: 0,
        size: 20,
      });
  }, [isOpen]);

  const handleFetchData = (params: GetExpenseListParams) => {
    const newParams = {
      ...params,
      status: 'SUBMITTED',
    };
    dispatch(dashboardActions.getExpenseList(newParams));
  };

  const refetchData = () => {
    handleFetchData({
      ...queryParams,
    });
    cbAfterAction();
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const columns: ColumnsType<IExpense> = [
    {
      key: 'code',
      title: 'Expense Code',
      dataIndex: 'code',
    },
    {
      key: 'subcontractorRef',
      title: 'Name Of Employee',
      dataIndex: 'subcontractorRef',
      render: (val) => <span>{val?.fullName}</span>,
    },
    {
      key: 'claimAmount',
      title: 'Amount',
      dataIndex: 'claimAmount',
      align: 'right',
      render: (value) =>
        formatMoney(value || 0, showCurrencyByCountry(payrollCountry)),
    },
  ];

  const { loading: approveExpenseLoading, run: approveExpenseRun } =
    useApproveExpense();
  const handleApprove = async () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: 'Please select at least one expense',
      });
      return;
    }
    const res = await approveExpenseRun(selectedRows.map((item) => item.id));
    if (res.success) {
      showMessage({
        type: 'success',
        content: 'Expenses approved successfully',
      });
      refetchData();
    }
  };

  const [isRejectExpenseModalOpen, setIsRejectExpenseModalOpen] =
    useState(false);
  const { loading: rejectExpenseLoading, run: rejectExpenseRun } =
    useRejectExpense();
  const handleOpenRejectExpenseModal = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: 'Please select at least one expense',
      });
      return;
    }
    setIsRejectExpenseModalOpen(true);
  };
  const handleReject = async (values: RejectExpenseValues) => {
    const res = await rejectExpenseRun({
      ids: selectedRows.map((item) => item.id),
      reason: values.reason,
    });
    if (res.success) {
      showMessage({
        type: 'success',
        content: 'Expenses rejected successfully',
      });
      refetchData();
    }
  };

  return (
    <AppModalContainer
      title={
        <div className="flex items-center justify-between w-full gap-x-[8px]">
          <div className='whitespace-nowrap'>Expenses</div>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'approve',
                  label: 'Approve',
                  onClick: handleApprove,
                },
                {
                  key: 'reject',
                  label: 'Reject',
                  onClick: handleOpenRejectExpenseModal,
                },
              ],
            }}
          >
            <AppButton
              name="Actions"
              rightIcon={<DownOutlined />}
              loading={approveExpenseLoading || rejectExpenseLoading}
              disabled={approveExpenseLoading || rejectExpenseLoading}
            />
          </Dropdown>
        </div>
      }
      isOpen={isOpen}
      toggle={toggle}
      hiddenFooter
      className="max-sm:w-[100vw]"
      width={1200}
      minHeight="0"
      noPaddingBody
      titleFullWidth
    >
      <AppsContent>
        <div className="flex items-center justify-between gap-2 w-full my-[20px] flex-wrap px-[10px]">
          <AppInputSearch onChange={handleSearch} />
        </div>
        <AppTableContainer
          rowKey="id"
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 800 }}
          columns={columns}
          onChangePagination={onChangePagination}
          rowSelection={{
            type: 'checkbox',
            onChange: (_: React.Key[], selectedRows: IExpense[]) => {
              setSelectedRows(selectedRows);
            },
          }}
        />
      </AppsContent>
      <RejectExpenseModal
        isOpen={isRejectExpenseModalOpen}
        toggle={() => setIsRejectExpenseModalOpen(false)}
        onSubmit={handleReject}
      />
    </AppModalContainer>
  );
};

export default ExpenseModal;
